import { useMemo, useState, useEffect } from 'react';
import { useTracking } from 'react-tracking';
import { ROUTE_COMPARE_COMPANY } from '@/constants/routes';
import { push } from 'redux-first-router';
import { getPath } from '@/router/utils';
import { MoreIcon } from './styled';
import { ButtonWithDropdown } from '@simplywallst/ui-core';
import If from '@/components/If';
import loadable from '@loadable/component';
import { Translation } from '@/hooks/useTranslation';

const GuestRegistrationModal = loadable(
  () => import('@components/GuestRegistrationModal'),
  { ssr: false }
);

const PlansModal = loadable(
  () => import('@components/OurPlans/components/PlansModal/PlansModal'),
  { ssr: false }
);

const AddRemoveCompany = loadable(() => import('@components/AddRemoveCompany'));

interface Props {
  companyId: string;
  tickerSymbol: string;
  canonicalUrl: string;
  uniqueSymbol: string;
  canGetReportPdf: boolean;
  canGetReportCsv: boolean;
  isUserLoggedIn: boolean;
  onFetchCompanyCsvReport: (companySymbol: string) => void;
  options: {
    label: string;
    value: string;
    icon: JSX.Element;
  }[];
}

type ActiveModal = 'none' | 'registration' | 'plans' | 'addRemoveCompany';

const CompareAndDownload = ({
  companyId,
  tickerSymbol,
  canonicalUrl,
  uniqueSymbol,
  canGetReportPdf,
  canGetReportCsv,
  isUserLoggedIn,
  onFetchCompanyCsvReport,
  options,
}: Props) => {
  const [activeModal, setActiveModal] = useState<ActiveModal>('none');
  const { trackEvent } = useTracking();
  const comparePath = useMemo(
    () =>
      getPath({
        type: ROUTE_COMPARE_COMPANY,
        payload: { symbols: encodeURIComponent(uniqueSymbol) },
      }),
    [uniqueSymbol]
  );

  useEffect(() => {
    if (isUserLoggedIn && activeModal === 'registration') {
      setActiveModal('addRemoveCompany');
    }
  }, [isUserLoggedIn, activeModal]);

  const handleSelect = (value: string) => {
    if (value === 'addToPortfolio') {
      trackEvent({
        type: 'track',
        action: 'click',
        subject: 'add to portfolio',
      });
      if (isUserLoggedIn) {
        setActiveModal('addRemoveCompany');
      } else {
        setActiveModal('registration');
        trackEvent({
          type: 'track',
          action: 'click',
          modifier: 'addToPortfolio',
          subject: 'guestRegistrationModal',
        });
      }
    } else if (value === 'PDF' && canGetReportPdf) {
      trackEvent({
        type: 'track',
        action: 'click',
        subject: 'Download PDF',
      });
      window.open(canonicalUrl + '?viewmode=print');
    } else if (value === 'CSV' && canGetReportCsv) {
      trackEvent({
        type: 'track',
        action: 'click',
        subject: 'Download CSV',
      });
      onFetchCompanyCsvReport(uniqueSymbol);
    } else if (value === 'compare') {
      trackEvent({
        type: 'track',
        action: 'click',
        subject: 'compare',
      });
      push(comparePath);
    } else {
      setActiveModal('plans');
    }
  };
  return (
    <>
      <ButtonWithDropdown
        marginLeft
        data-cy-id="company-compare-and-download"
        styleType="solid"
        options={options}
        onSelect={handleSelect}
      >
        <MoreIcon />
      </ButtonWithDropdown>

      <If
        conditions={{
          showPlansModal: activeModal === 'plans',
          registration: activeModal === 'registration' && !isUserLoggedIn,
          showAddRemoveCompany: activeModal === 'addRemoveCompany',
        }}
      >
        {{
          showPlansModal: () => (
            <PlansModal onClose={() => setActiveModal('none')}>
              <Translation i18nKey="page.companyReport.header.warning.downloadUnavailable">
                <p>
                  Download Company Report is only available on Unlimited Plan.
                </p>
                <h2>Upgrade to unlock Company Report Download</h2>
              </Translation>
            </PlansModal>
          ),
          registration: () => (
            <GuestRegistrationModal
              canClose
              onClose={() => setActiveModal('none')}
              onRegisterSuccess={() => {
                trackEvent({
                  type: 'track',
                  subject: 'compareAndDownload',
                  modifier: 'registration',
                  action: 'success',
                });
              }}
            />
          ),
          showAddRemoveCompany: () => (
            <AddRemoveCompany
              onClose={() => setActiveModal('none')}
              tickerSymbol={tickerSymbol}
              companyId={companyId}
              uniqueSymbol={uniqueSymbol}
            />
          ),
        }}
      </If>
    </>
  );
};

export default CompareAndDownload;
